import { useEffect, useState } from "react";
import {
    Configure,
    connectHits,
    InstantSearch,
    Pagination,
    SearchBox,
    SortBy,
} from "react-instantsearch-dom";
import { OrganizationStatus } from "../../models/enumerations/organization-status";
import { OrganizationStatusLabel } from "../../models/organization-status-labels";
import { PlanTypeLabels } from "../../models/plan-type";
import { PlanType } from "../../models/enumerations/plan-type";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import searchClient, { SearchIndexes } from "../../utilities/search-client";
import OrganizationService from "../../utilities/services/organization-service";
import FunctionService from "../../utilities/services/function-service";
import OrganizationSubscriptionService from "../../utilities/services/organization-subscription-service";
import UserUtil from "../../utilities/user-util";
import { createLogger } from "../../utilities/logger";
import { Button } from "../button/button";
import { Loader } from "../loader/loader";
import { Modal } from "../modal/modal";
import { SortIcons } from "../sort-icons/sort-icons";
import { useSnackbar } from "notistack";
import { useTranslation } from 'react-i18next';

const log = createLogger("OrganizationListTable");

const COMPONENT_CLASS = "c-organization-list";

export type OrganizationListTableProps = {
    /** Algolia filter limiting the visible organizations. */
    algoliaFilters: string;
    /** Whether the plan and status columns are shown (super admin only). */
    showPlanStatus: boolean;
    /** Called when a row's edit/view action is clicked. */
    onEdit: (hit: any) => void;
    /** Called when a row is clicked (super admin opens the view modal). */
    onRowClick?: (hit: any) => void;
    /** Called after an organization was deleted or merged successfully. */
    onDeleted?: () => void;
};

/**
 * Shared organization list table (search, sorting, pagination, row actions
 * and deletion/merge). Role-specific chrome (header, tiles, modals) lives in
 * the super admin / sub-organization containers.
 */
export const OrganizationListTable: React.FC<OrganizationListTableProps> = (props: OrganizationListTableProps) => {
    const {
        algoliaFilters,
        showPlanStatus,
        onEdit,
        onRowClick,
        onDeleted,
    } = props;

    const [isSaving, setIsSaving] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [organizationToDelete, setOrganizationToDelete] = useState<any>({});
    const [parentOrgName, setParentOrgName] = useState<string>();
    const [hasSubOrganizations, setHasSubOrganizations] = useState<boolean>(false);
    const [isCheckingSubOrgs, setIsCheckingSubOrgs] = useState<boolean>(false);
    const [searchState, setSearchState] = useState({
        sortBy: SearchIndexes.organizationsByNameAsc,
    });

    const { state, setState } = useAuthState();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const clearSearchCache = async () => {
        return Promise.resolve(
            setSearchState({
                sortBy: SearchIndexes.organizationsByNameAsc,
            })
        );
    };

    const handleDeleteOrganizationClick = async () => {
        if (organizationToDelete?.objectID == null) {
            return;
        }

        // Parent organizations that still have sub-organizations cannot be
        // deleted: cascade deleting sub-organizations is not available.
        if (hasSubOrganizations) {
            log.warn("Organization deletion blocked: parent organization has sub-organizations", {
                organizationId: organizationToDelete.objectID,
                organizationName: organizationToDelete.name,
            });
            return;
        }

        const organizationId = organizationToDelete.objectID;
        const parentOrganizationId = organizationToDelete.parentOrganizationId;

        setIsSaving(true);

        try {
            if (parentOrganizationId) {
                // Deleting a sub-organization merges its users, groups, tags
                // and links into the parent organization before removing it.
                await FunctionService.mergeSubOrganization(organizationId, parentOrganizationId);
                log.info("Sub-organization merged into parent", {
                    organizationId,
                    parentOrganizationId,
                });
            } else {
                await OrganizationService.deleteById(organizationId);
                log.info("Organization deleted", {
                    organizationId,
                });
            }

            // if the deleted org has a subscription, we want to delete it
            const subResponse = await OrganizationSubscriptionService.getByOrganizationId(organizationId);
            if (subResponse && subResponse.length > 0 && subResponse[0].id) {
                await OrganizationSubscriptionService.deleteById(subResponse[0].id);
            }

            // if the deleted org was a sub-organization, we want to update the
            // parent organization's state to reflect the change
            if (parentOrganizationId) {
                const updatedParentOrg = await OrganizationService.get(parentOrganizationId);
                if (state.organization.id === parentOrganizationId && updatedParentOrg) {
                    setState((state) => ({
                        ...state, ...{ organization: updatedParentOrg }
                    }));
                }
            }

            setOpenDelete(false);
            setOrganizationToDelete(undefined);
            clearSearchCache().then(() => setIsSaving(false));

            enqueueSnackbar(
                parentOrganizationId
                    ? t('organization.mergeSubOrg_confirm', { parentOrgName: parentOrgName ?? '' })
                    : t('organization.del_confirm'),
                { variant: "toast", width: "450px" }
            );

            onDeleted && onDeleted();
        } catch (e) {
            log.error("Failed to delete organization", {
                exception: e,
                organizationId,
                parentOrganizationId,
            });
            setIsSaving(false);
            enqueueSnackbar(t('organization.del_error'), { variant: "error" });
        }
    };

    useEffect(() => {
        clearSearchCache().then(() => setIsSaving(false));
    }, []);

    useEffect(() => {
        if (!openDelete) {
            setOrganizationToDelete(undefined);
            setParentOrgName(undefined);
            setHasSubOrganizations(false);
            setIsCheckingSubOrgs(false);
        }
    }, [openDelete]);

    const updateSort = (sortBy: string | undefined) => {
        const state = Object.assign({}, searchState);
        state.sortBy = sortBy!;
        setSearchState(state);
    };

    const isAccountHoldersOriginalOrg = (id: string): boolean => {
        if (UserUtil.isAccountHolder(state.user)) {
            if (id === state.user?.organizationId) {
                return true;
            }
        }
        return false;
    };

    const customHits = ({ hits }: any) => {
        return (
            <div className={`list__table-container`}>
                <table className={`${COMPONENT_CLASS}__list__table`}>
                    <thead>
                        <tr>
                            <th className="py-2">
                                <div className="sort-icons-container">
                                    <span>{t('organization.edit.fields.orgName')}</span>
                                    <SortIcons
                                        ascField={SearchIndexes.organizationsByNameAsc}
                                        descField={SearchIndexes.organizationsByNameDesc}
                                        currentSort={searchState.sortBy}
                                        onSortClick={(sortBy) => updateSort(sortBy)}
                                    />
                                </div>
                            </th>
                            <th className="py-2">
                                <div className="sort-icons-container">
                                    <span>{t('general.users')}</span>
                                    <SortIcons
                                        ascField={SearchIndexes.organizationsByUsersAsc}
                                        descField={SearchIndexes.organizationsByUsersDesc}
                                        currentSort={searchState.sortBy}
                                        onSortClick={(sortBy) => updateSort(sortBy)}
                                    />
                                </div>
                            </th>
                            <th className="py-2">
                                <span>{t('organization.programs')}</span>
                            </th>
                            {showPlanStatus &&
                                <>
                                    <th className="py-2">
                                        <div className="sort-icons-container">
                                            <span>{t('organization.edit.fields.plan')}</span>
                                            <SortIcons
                                                ascField={SearchIndexes.organizationsByPlanAsc}
                                                descField={SearchIndexes.organizationsByPlanDesc}
                                                currentSort={searchState.sortBy}
                                                onSortClick={(sortBy) => updateSort(sortBy)}
                                            />
                                        </div>
                                    </th>
                                    <th className="py-2">
                                        <div className="sort-icons-container">
                                            <span>{t('organization.edit.fields.status')}</span>
                                            <SortIcons
                                                ascField={SearchIndexes.organizationsByStatusAsc}
                                                descField={SearchIndexes.organizationsByStatusDesc}
                                                currentSort={searchState.sortBy}
                                                onSortClick={(sortBy) => updateSort(sortBy)}
                                            />
                                        </div>
                                    </th>
                                </>}
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {hits.map((h: any, i: number) => (
                            <tr
                                className={`${COMPONENT_CLASS}__item -${typeof OrganizationStatus[h.status] === 'string' ? OrganizationStatus[
                                    h.status
                                ].toLowerCase() : "undefined"}`}
                                key={`organization-list-${i}`}
                                onClick={() => { onRowClick && onRowClick(h) }}
                            >
                                <td><div>{h.name}</div></td>
                                <td className={`users-cell ${(h.userCount / h.userLimit >= 0.90 && h.userLimit > 0) ? 'red' : ''}`}>
                                    <div className="show-on-mobile mr-1"><strong>{t('organization.users_caps')}</strong></div>
                                    <div>{h.userCount || "0"}{h.userLimit > 0 ? `/${h.userLimit}` : "/no limit"}</div>
                                </td>
                                <td className={`programs-cell ${(h.mySymmioLicenseCount / h.mySymmioLicense >= 0.90 && h.mySymmioLicense > 0) ? 'red' : ''}`}>
                                    <div className="show-on-mobile mr-1"><strong>{t('organization.programs_caps')}</strong></div>
                                    <div>{h.mySymmioLicenseCount ?? 0}{h.mySymmioLicense > 0 ? `/${h.mySymmioLicense}` : "/no limit"}</div>
                                </td>
                                {showPlanStatus &&
                                    <>
                                        <td>
                                            <div className="show-on-mobile mr-1"><strong>{t('organization.plan_caps')}</strong></div>
                                            <div>{h.plan?.value !== undefined ? PlanTypeLabels[h.plan.value as PlanType] : ""}</div>
                                        </td>
                                        <td
                                            className="status-cell"><div>{OrganizationStatusLabel[h.status as OrganizationStatus]}</div>
                                        </td>
                                    </>}
                                <td>
                                    <Button
                                        type="table-action"
                                        disabled={h.plan?.value === PlanType.Partner}
                                        buttonText={isAccountHoldersOriginalOrg(h.id) ? t('buttons.btn_view') : t('buttons.btn_edit')}
                                        buttonStyle="white"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(h);
                                        }}
                                    />
                                    <Button
                                        type="table-action"
                                        buttonText={t('buttons.btn_delete')}
                                        buttonStyle="white"
                                        onClick={(e) => {
                                            setOrganizationToDelete(h);
                                            setParentOrgName(undefined);
                                            setHasSubOrganizations(false);
                                            if (h.parentOrganizationId) {
                                                setParentOrgName(h.parentOrganizationId);
                                                OrganizationService.get(h.parentOrganizationId)
                                                    .then((parent) => setParentOrgName(parent?.name || h.parentOrganizationId))
                                                    .catch(() => setParentOrgName(h.parentOrganizationId));
                                            } else {
                                                // Parent organizations with sub-organizations cannot be deleted.
                                                setIsCheckingSubOrgs(true);
                                                OrganizationService.getBy([{
                                                    field: 'parentOrganizationId',
                                                    operator: '==',
                                                    value: h.objectID,
                                                }])
                                                    .then((subOrgs) => setHasSubOrganizations((subOrgs?.length ?? 0) > 0))
                                                    .catch((e) => {
                                                        log.error("Failed to check for sub-organizations on delete", {
                                                            exception: e,
                                                            organizationId: h.objectID,
                                                        });
                                                        setHasSubOrganizations(false);
                                                    })
                                                    .finally(() => setIsCheckingSubOrgs(false));
                                            }
                                            setOpenDelete(true);
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        disabled={isAccountHoldersOriginalOrg(h.id)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const CustomHits = connectHits(customHits);

    return (
        <>
            <Loader isVisible={isSaving} />
            <InstantSearch
                indexName={SearchIndexes.organizationsByNameAsc}
                searchClient={searchClient}
                searchState={searchState}
                onSearchStateChange={setSearchState}
            >
                <Configure
                    filters={algoliaFilters}
                    hitsPerPage={50}
                />
                <SortBy
                    items={[
                        {
                            label: t('organization.edit.sorting.nameAsc'),
                            value: SearchIndexes.organizationsByNameAsc,
                        },
                        {
                            label: t('organization.edit.sorting.nameDesc'),
                            value: SearchIndexes.organizationsByNameDesc,
                        },
                        {
                            label: t('organization.edit.sorting.userCountAsc'),
                            value: SearchIndexes.organizationsByUsersAsc,
                        },
                        {
                            label: t('organization.edit.sorting.userCountDesc'),
                            value: SearchIndexes.organizationsByUsersDesc,
                        },
                        {
                            label: t('organization.edit.sorting.planAsc'),
                            value: SearchIndexes.organizationsByPlanAsc,
                        },
                        {
                            label: t('organization.edit.sorting.planDesc'),
                            value: SearchIndexes.organizationsByPlanDesc,
                        },
                        {
                            label: t('organization.edit.sorting.statusAsc'),
                            value: SearchIndexes.organizationsByStatusAsc,
                        },
                        {
                            label: t('organization.edit.sorting.statusDesc'),
                            value: SearchIndexes.organizationsByStatusDesc,
                        },
                    ]}
                    defaultRefinement="organizations"
                />
                <SearchBox
                    autoFocus
                    className={`${COMPONENT_CLASS}__search`}
                    searchAsYouType={true}
                    showLoadingIndicator={true}
                    loadingIndicator={<Loader isVisible={true} />}
                    translations={{
                        placeholder: t('general.search')
                    }}
                />
                <CustomHits />
                <Pagination className={`${COMPONENT_CLASS}__pagination`} />
            </InstantSearch>
            <Modal
                isOpen={openDelete}
                isLoading={isSaving}
                onClose={setOpenDelete}
                title={t('organization.deleteOrganization')}
                defaultModalActions={true}
                submitDisabled={isSaving || isCheckingSubOrgs || hasSubOrganizations}
                onSubmit={() => handleDeleteOrganizationClick()}
                onCancel={() => setOpenDelete(false)}
                submitButtonText={t('buttons.btn_deleteOrg')}
            >
                <div>
                    {hasSubOrganizations ? (
                        <p>
                            {t('organization.deleteBlockedBySubOrgs')}
                        </p>
                    ) : organizationToDelete?.parentOrganizationId ? (
                        <p>
                            {t('organization.mergeSubOrg_desc', { parentOrgName: parentOrgName ?? '' })}
                        </p>
                    ) : (
                        <p>
                            {t('organization.del_confirm_desc')}{" "}
                            {organizationToDelete?.name}.
                        </p>
                    )}
                </div>

            </Modal>
        </>
    );
};

export default OrganizationListTable;
